import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../../app.reducers';
import { Observable } from 'rxjs';
import { Property, PropertyReserveStudy, PropertyType } from '../../../property/shared/models';
import { ToastrService } from 'ngx-toastr';
import { AddPropertyReserveStudyAction, DeletePropertyReserveStudyAction, GetAllPropertyReserveStudyAction, RemovePropertyProfileImageAction, RemovePropertyProfileImageSuccessAction, UpdatePropertyReserveStudyAction, propertyActionTypes } from '../../../property/property-store/property.actions';
import { UserProfile } from '../../../user/models';
import { getUserProfile } from '../../../user/redux/user.selectors';
import { getAdminProperty, getAdminPropertySaveError, getIntegrationProperty } from '../../admin-store/admin.selectors';
import { SubSink } from 'subsink';
import { IntegrationProperty } from '../../shared/models/integration-property';
import { Tab } from 'app/shared';
import { Invitation, Role } from 'app/internal/workspace/shared/models';
import { getRolesForRoleDropdown } from 'app/internal/workspace/workspace-store/workspace.selectors';
import { LoadWorkspaceRolesAction } from 'app/internal/workspace/workspace-store';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { getPropertyReserveStudies, getPropertyTypes } from 'app/internal/property/property-store/property.selectors';
import { getAdminEditPortfolioListTab, getAdminWorkspace } from '../../admin-store/admin.selectors';
import { Workspace, WorkspaceProperty, WorkspaceUser } from 'app/internal/shared';
import {
  ClearAdminImportRequestsAction,
  ClearAdminPortfolioAction,
  ClearAdminPropertyAction,
  LoadAdminIntegrationPropertyAction,
  LoadAdminIntegrationsAction,
  LoadAdminPortfolioAction,
  LoadAdminPropertyAction,
  SaveAdminPortfolioInviteAction,
  SetAdminEditPortfolioTabAction,
  ToggleAdminPropertyActiveStatusAction,
  ToggleAdminUserActiveStatusAction,
  UpdateAdminPropertyDetailsAction,
  UpdateAdminWorkspaceUserAction
} from '../../admin-store/admin.actions';
import { Actions, ofType } from '@ngrx/effects';

@Component({
  templateUrl: './admin-portfolio-edit-property.component.html',
  styleUrls: ['./admin-portfolio-edit-property.component.scss'],
  standalone: false
})
export class AdminPortfolioEditPropertyComponent implements OnInit, OnDestroy {

  propertyTypes$: Observable<PropertyType[]>;
  property$: Observable<Property>;
  workspace$: Observable<Workspace>;
  userProfile$: Observable<UserProfile>;
  propertyId: string;
  workspaceId: string;
  roles$: Observable<Role[]>;
  roles: Role[];
  activeTabIndex = 0;
  workspaceUsers: WorkspaceUser[];
  workspaceProperties: WorkspaceProperty[];
  integrationProperty$: Observable<IntegrationProperty>;
  reserveStudies$: Observable<PropertyReserveStudy[]>;
  adminPropertySaveError$: Observable<string | null>;
  tabs: Tab[];
  currentTab: Tab;
  selectedTab$: Observable<string>;
  readonly tabGeneral = 'General';
  readonly tabUsers = 'Users';
  readonly tabConnectedApps = 'Connected Apps';
  readonly tabReserveStudyHistory = 'Reserve Study History';
  portfolioBreadCrumbText: string;

  navigatedFromPropertyList = false;

  private subs = new SubSink();

  constructor(private router: Router,
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private actions$: Actions) {
    this.property$ = this.store.pipe(select(getAdminProperty));
    this.propertyTypes$ = this.store.pipe(select(getPropertyTypes));
    this.userProfile$ = this.store.pipe(select(getUserProfile));
    this.workspace$ = this.store.pipe(select(getAdminWorkspace));
    this.selectedTab$ = this.store.pipe(select(getAdminEditPortfolioListTab));
    this.roles$ = this.store.pipe(select(getRolesForRoleDropdown));
    this.integrationProperty$ = this.store.pipe(select(getIntegrationProperty));
    this.reserveStudies$ = this.store.pipe(select(getPropertyReserveStudies));
    this.adminPropertySaveError$ = this.store.pipe(select(getAdminPropertySaveError));
  }

  ngOnInit(): void {

    this.route.params.subscribe(
      (params: Params) => {
        this.workspaceId = params['workspaceId'];
        this.propertyId = params['id'];
        this.store.dispatch(new LoadAdminPortfolioAction({ workspaceId: this.workspaceId, includeIntegrationDetail: false }));
        this.store.dispatch(new LoadWorkspaceRolesAction());
        this.store.dispatch(new LoadAdminIntegrationsAction());
        this.store.dispatch(new LoadAdminIntegrationPropertyAction(this.propertyId));
        this.store.dispatch(new LoadAdminPropertyAction(this.propertyId));
        this.store.dispatch(new LoadWorkspaceRolesAction());
        this.store.dispatch(new GetAllPropertyReserveStudyAction(this.propertyId));
      }
    );

    this.subs.sink = this.workspace$.subscribe(workspace => {
      if (workspace) {
        if (workspace.companyName && workspace.companyName !== '') {
          this.portfolioBreadCrumbText = workspace.companyName;
        } else {
          this.portfolioBreadCrumbText = workspace.name;
        }
        this.workspaceProperties = workspace?.workspaceProperties;

        // Only show the users who have access to this Property        
        this.workspaceUsers = workspace?.workspaceUsers?.filter(user => {
          return user.properties.filter(a => a.propertyId === this.propertyId).length > 0;
        });
      }
    });

    // Set tabs & selected tab
    this.tabs = this.getTabsData();
    this.setSelectedTab();

    // Updated the selected tab, if we have one
    this.subs.sink = this.selectedTab$
      .pipe(distinctUntilChanged())
      .subscribe((selectedTab: string) => {

        if (selectedTab == null || selectedTab === '') {
          return;
        }

        const index = this.tabs.findIndex(x => x.name === selectedTab);
        if (index && index !== -1) {
          this.setSelectedTab(this.tabs[index]);
        }
      });

    // select tab from route param (if any)
    this.subs.sink = this.route.queryParams.pipe(take(1))
      .subscribe(params => {
        const tabIndex = params['tabIndex'];
        const navigatedFromPropertyList = params['navigatedFromPropertyList'];
        if (tabIndex) {
          this.setSelectedTab(this.tabs[tabIndex]);
        }
        if (navigatedFromPropertyList) {
          this.navigatedFromPropertyList = true;
        }
      });

    this.subs.sink = this.actions$.pipe(
      ofType<RemovePropertyProfileImageSuccessAction>(propertyActionTypes.REMOVE_PROPERTY_PROFILE_IMAGE_SUCCESS),
    ).subscribe(() => {
      this.store.dispatch(new LoadAdminPropertyAction(this.propertyId));
    });
  }

  ngOnDestroy() {
    this.store.dispatch(new ClearAdminImportRequestsAction());
    this.store.dispatch(new ClearAdminPropertyAction());
    this.store.dispatch(new ClearAdminPortfolioAction());
    this.store.dispatch(new SetAdminEditPortfolioTabAction(this.tabs[0].name)); // Always reset the Tab back to the first tab
    this.subs.unsubscribe();
  }

  onCancelProperty() {
    if (!this.navigatedFromPropertyList) {
      this.router.navigate(['/useraccount/admin/portfolios/' + this.workspaceId], { queryParams: { tabIndex: '1' } });
    } else {
      this.router.navigate(['/useraccount/admin/properties']);
    }
  }

  onSaveProperty(property: Property) {
    if (!this.navigatedFromPropertyList) {
      this.store.dispatch(new UpdateAdminPropertyDetailsAction({ property: property, workspaceId: this.workspaceId }));
    } else {
      this.store.dispatch(new UpdateAdminPropertyDetailsAction({ property: property }));
    }
  }

  onSavePropertyImage() {
    //this.store.dispatch(new LoadSelectedPropertyAction(new LoadPropertyRequest(propertyId, false, null, true)));
    this.store.dispatch(new LoadAdminPropertyAction(this.propertyId));
    this.toastr.success('Profile Image Saved');
  }

  onRemovePropertyImage(propertyId: string) {
    this.store.dispatch(new RemovePropertyProfileImageAction(propertyId));
    this.toastr.success('Profile Image Removed');
  }

  onTabClick(selectedTab: Tab) {
    if (this.shouldChangeSelectedTab(selectedTab)) {
      this.store.dispatch(new SetAdminEditPortfolioTabAction(selectedTab.name));
      this.setSelectedTab(selectedTab);
    }
  }

  onUpdateRole(user: WorkspaceUser) {
    // Updating roleName to selected value
    this.store.dispatch(new UpdateAdminWorkspaceUserAction({ workSpaceUser: user, workSpaceId: this.workspaceId }));
  }

  onInviteUser(invite: Invitation) {
    this.store.dispatch(new SaveAdminPortfolioInviteAction({ invite: invite, workSpaceId: this.workspaceId }));
  }

  onDeactivateProperty(propertyId: string) {
    this.store.dispatch(new ToggleAdminPropertyActiveStatusAction({ propertyId: propertyId, isActive: false, workspaceId: this.workspaceId }));
  }

  onReactivateProperty(propertyId: string) {
    this.store.dispatch(new ToggleAdminPropertyActiveStatusAction({ propertyId: propertyId, isActive: true, workspaceId: this.workspaceId }));
  }

  onDeactivateUser(userId: string) {
    this.store.dispatch(new ToggleAdminUserActiveStatusAction({ userId: userId, isActive: false, workspaceId: this.workspaceId }));
  }

  onReactivateUser(userId: string) {
    this.store.dispatch(new ToggleAdminUserActiveStatusAction({ userId: userId, isActive: true, workspaceId: this.workspaceId }));
  }

  onEditUser(userId) {
    this.router.navigate(['/useraccount/admin/portfolios/' + this.workspaceId + '/edit-user/' + userId]);
  }

  onAddReserveStudy(propertyReserveStudy: PropertyReserveStudy) {
    this.store.dispatch(new AddPropertyReserveStudyAction(propertyReserveStudy));
  }

  onUpdateReserveStudy(propertyReserveStudy: PropertyReserveStudy) {
    this.store.dispatch(new UpdatePropertyReserveStudyAction(propertyReserveStudy));
  }

  onDeleteReserveStudy(propertyReserveStudy: PropertyReserveStudy) {
    this.store.dispatch(new DeletePropertyReserveStudyAction(
      { propertyId: propertyReserveStudy.propertyId, propertyReserveStudyId: propertyReserveStudy.propertyReserveStudyId }
    ));
  }

  private getTabsData() {
    return <Tab[]>[
      {
        name: this.tabGeneral,
        title: 'General',
        isActive: this.isActiveTab(this.tabGeneral)
      },
      {
        name: this.tabUsers,
        title: 'Users',
        isActive: this.isActiveTab(this.tabUsers)
      },
      {
        name: this.tabConnectedApps,
        title: 'Connected Apps',
        isActive: this.isActiveTab(this.tabConnectedApps)
      },
      {
        name: this.tabReserveStudyHistory,
        title: 'Reserve Study History',
        isActive: this.isActiveTab(this.tabReserveStudyHistory)
      }
    ];
  }

  private isActiveTab(currentTab?: string) {
    return (this.currentTab && currentTab === this.currentTab.name);
  }

  private setSelectedTab(selectedTab?: Tab) {

    // If a tab is passed in get its index, else default to 0
    const index = (selectedTab)
      ? this.tabs.findIndex(x => x.name === selectedTab.name)
      : 0;

    // Update all tabs to not be active
    this.tabs.map(x => x.isActive = false);

    // Set the selected tab
    this.tabs[index].isActive = true;
    this.currentTab = this.tabs[index];
  }

  private shouldChangeSelectedTab(selectedTab: Tab) {
    return selectedTab.name !== this.currentTab.name;
  }
}
